import React, { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Copy, MessageCircle, Star, Sparkles } from 'lucide-react';

export const GOOGLE_REVIEW_URL = 'https://www.google.com/search?q=SG+Cabs+Bengaluru&ludocid=14077928454062450918#lrd=0x3bae13a19e54156b:0xc35ed95745c1a8e6,3';
const REVIEW_LINK = 'https://sg-cabs.com/review';

const templates = [
  {
    id: 'warm',
    label: 'Warm & Personal',
    build: (name) =>
      `Hi ${name}! 🙏 This is SG Cabs.\n\nWe hope you reached safely and your trip with us was smooth and comfortable. It was a pleasure driving you today!\n\nIf you have a moment, we'd be grateful for a quick Google review — it helps our small Bengaluru team grow and lets other travellers find a reliable cab:\n⭐ ${REVIEW_LINK}\n\nThank you for choosing SG Cabs. We look forward to your next ride! 🚖\n\n— Team SG Cabs | +91 75078 67648`,
  },
  {
    id: 'short',
    label: 'Short & Sweet',
    build: (name) =>
      `Hi ${name}, thank you for riding with SG Cabs today! 🚖 We hope the journey was safe and comfortable.\n\nLoved the ride? A 30-second Google review would mean the world to us ⭐\n${REVIEW_LINK}\n\nSee you on your next trip! — SG Cabs`,
  },
  {
    id: 'premium',
    label: 'Premium Touch',
    build: (name) =>
      `Dear ${name},\n\nThank you for travelling with SG Cabs. We trust your journey was safe, punctual and relaxing — exactly the gold standard we promise every guest.\n\nYour feedback shapes our service. If we earned it, please share a star rating and a few words on Google:\n⭐ ${REVIEW_LINK}\n\nIt would be our privilege to serve you again — airport transfers, outstation trips or one way drops across South India.\n\nWarm regards,\nTeam SG Cabs · +91 75078 67648`,
  },
];

export const ReviewRequestPage = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [templateId, setTemplateId] = useState('warm');

  const cleanName = name.trim() || 'there';
  const message = templates.find((t) => t.id === templateId).build(cleanName);
  const digits = phone.replace(/\D/g, '');
  const waNumber = digits.length === 10 ? `91${digits}` : digits;

  const copy = async () => {
    await navigator.clipboard.writeText(message);
    toast.success('Message copied');
  };

  const openWhatsApp = () => {
    const base = waNumber ? `https://wa.me/${waNumber}` : 'https://wa.me/';
    window.open(`${base}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
  };

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-6" data-testid="review-request-page">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Star className="w-6 h-6 text-yellow-500 fill-yellow-500" /> Ask for a Google Review
        </h2>
        <p className="text-gray-500 text-sm mt-1">Enter the customer's name, pick a tone, then copy or send on WhatsApp.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6 space-y-5">
            <div>
              <Label htmlFor="rr-name">Customer name</Label>
              <Input id="rr-name" data-testid="review-customer-name" placeholder="e.g. Shubham" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="rr-phone">WhatsApp number <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Input id="rr-phone" data-testid="review-customer-phone" placeholder="e.g. 9876543210" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Message style</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`review-template-${t.id}`}
                    onClick={() => setTemplateId(t.id)}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      templateId === t.id ? 'border-black bg-black text-white' : 'border-gray-200 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button data-testid="review-copy-btn" onClick={copy} variant="outline" className="flex-1">
                <Copy className="w-4 h-4 mr-2" /> Copy message
              </Button>
              <Button data-testid="review-whatsapp-btn" onClick={openWhatsApp} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                <MessageCircle className="w-4 h-4 mr-2" /> Send on WhatsApp
              </Button>
            </div>
            <p className="text-xs text-gray-400">
              <span className="font-medium text-gray-500">sg-cabs.com/review</span> sends the customer straight to Google's "Rate and review SG Cabs" box — no map page in between.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-[#0a0a0a] border-[#d4af37]/30">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-[#d4af37] text-xs font-semibold uppercase tracking-[0.15em] mb-4">
              <Sparkles className="w-4 h-4" /> Preview
            </div>
            <pre data-testid="review-message-preview" className="whitespace-pre-wrap font-sans text-sm text-gray-200 leading-relaxed bg-[#121212] border border-white/10 rounded-xl p-4">
              {message}
            </pre>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
